import { Loading } from '@iheartjane/reefer';
import type {
  ActionFunctionArgs,
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from 'react-router';
import { data, useLoaderData } from 'react-router';

import {
  clearCartAction,
  refreshCartAction,
} from '@jane/bloom/actions/cart.server';
import { useIsNavigatingTo } from '@jane/bloom/hooks/navigation/useIsNavigating';
import { globalSubloader } from '@jane/bloom/loaders/globalSubloader.server';
import { MENU_CONTEXT } from '@jane/bloom/middlewares/menuContext.server';
import { canonicalTag } from '@jane/bloom/resources/meta/canonicalTag';
import { description } from '@jane/bloom/resources/meta/description';
import { getRootLoaderData } from '@jane/bloom/resources/meta/getRootLoaderData';
import { noindex } from '@jane/bloom/resources/meta/noindex';
import { parentMeta } from '@jane/bloom/resources/meta/parentMeta';
import { preconnectLinks } from '@jane/bloom/resources/meta/preconnectLinks';
import { title } from '@jane/bloom/resources/meta/title';
import { getCartInfo } from '@jane/bloom/sessions/cart.server';
import { getUserIdentity } from '@jane/bloom/sessions/userIdentity.server';
import type { CheckoutViewModel } from '@jane/bloom/viewModels/checkout.server';
import { buildCheckoutViewModel } from '@jane/bloom/viewModels/checkout.server';

import { CartAction } from './types';
import { useInitializeCart } from './useInitializeCart';

import stylesLink from './styles.css?url';

export const links: LinksFunction = () => [
  { href: stylesLink, rel: 'stylesheet' },
];

export const meta: MetaFunction<typeof loader> = (args) => {
  const {
    appConfig: { headlessEmbedLink },
    canonicalMenuUrl,
    pinnedStore,
  } = getRootLoaderData(args);
  return [
    title('Bag', pinnedStore.name),
    description(),
    canonicalTag(args.location, canonicalMenuUrl),
    noindex(),
    ...preconnectLinks([headlessEmbedLink]),
    ...parentMeta(args),
  ];
};

type CheckoutLoaderData = {
  checkoutData: CheckoutViewModel;
};

export async function loader({ context, request, url }: LoaderFunctionArgs) {
  const cookie = request.headers.get('Cookie');
  const searchParams = new URLSearchParams(url.searchParams);

  const { menu } = context.get(MENU_CONTEXT);
  const { shoppingPreferences } = await globalSubloader(request, menu);
  const cart = await getCartInfo(cookie, menu.cookie_prefix);
  const userIdentity = await getUserIdentity(request, menu.cookie_prefix);

  const checkoutData = buildCheckoutViewModel({
    cart,
    deliveryAddress: {
      city: shoppingPreferences.city ?? '',
      country_code: shoppingPreferences.countryCode ?? 'US',
      lat: shoppingPreferences.latitude ?? 0,
      lng: shoppingPreferences.longitude ?? 0, // Passing `long` and `lng`
      long: shoppingPreferences.longitude ?? 0, // so that the API can use either
      state_code: shoppingPreferences.state ?? '',
      street: shoppingPreferences.streetAddress,
      street2: shoppingPreferences.streetAddress2,
      zipcode: shoppingPreferences.zipcode,
    },
    jwt: userIdentity?.jwtToken,
    menu,
    skipBag: Boolean(searchParams.get('skipBag')),
    storeId: shoppingPreferences.storeId,
    user: userIdentity?.user,
  });

  const result: CheckoutLoaderData = {
    checkoutData,
  };
  return result;
}

export async function action(args: ActionFunctionArgs) {
  const intent = (await args.request.clone().formData()).get('intent');

  switch (intent) {
    case CartAction.ClearCart: {
      return await clearCartAction(args);
    }
    case CartAction.RefreshCart: {
      return await refreshCartAction(args);
    }
    default: {
      return data({ message: `Action "${intent}" not found` }, { status: 400 });
    }
  }
}

export default function CheckoutRoute() {
  const { checkoutData } = useLoaderData<typeof loader>();

  useInitializeCart({ checkoutData });

  if (useIsNavigatingTo(/.*\/checkout\/confirmation\/.*/)) {
    return <Loading color="black" size="lg" />;
  }

  // The route itself does not draw any content; the headless cart (loaded from the Root component)
  // is responsible for drawing the UI. We also need to register the event listeners for events emitted
  // from the headless cart.
  return null;
}
